import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, DollarSign, UtensilsCrossed, Sparkles, TrendingUp, Building2, Layers } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCLP = (n) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat('es-CL').format(n || 0);
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + 'T00:00:00'); d.setDate(d.getDate() + n); return iso(d); };

const COLOR_HEX = {
  emerald: '#10b981', amber: '#f59e0b', lime: '#84cc16', sky: '#0ea5e9',
  indigo: '#6366f1', violet: '#8b5cf6', rose: '#f43f5e', orange: '#f97316', slate: '#64748b',
};

export default function SgCasinoReporte() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const hoy = iso(new Date());
  const [desde, setDesde] = useState(addDays(hoy, -29));
  const [hasta, setHasta] = useState(hoy);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/servicios-generales/casino/reporte`, {
        params: { desde, hasta }, headers,
      });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar reporte'); }
    finally { setLoading(false); }
  }, [desde, hasta, token]);

  useEffect(() => { load(); }, [load]);

  const seedDemo = async () => {
    if (!window.confirm('¿Generar ~360 usos demo distribuidos en los últimos 30 días?')) return;
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/servicios-generales/casino/seed-demo?dias=30&usos_por_dia=12`, {}, { headers });
      toast.success(`Demo generada: ${r.data.insertados} usos.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-4" data-testid="sg-casino-reporte-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reportes Casino</h1>
          <p className="text-sm text-slate-500">Consumos por período con desglose por tipo de servicio, centro de costo y empresa.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
            <Calendar className="w-4 h-4 text-emerald-700" />
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
              className="text-sm border-0 focus:outline-none w-32" data-testid="rpt-desde" />
            <span className="text-slate-400">→</span>
            <input type="date" value={hasta} min={desde} onChange={e => setHasta(e.target.value)}
              className="text-sm border-0 focus:outline-none w-32" data-testid="rpt-hasta" />
          </div>
          <button onClick={seedDemo} disabled={seeding}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-sm font-semibold rounded-lg disabled:opacity-50"
            data-testid="btn-casino-seed">
            <Sparkles className="w-4 h-4" /> {seeding ? 'Generando…' : 'Generar demo'}
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-sm text-slate-400 py-6">Cargando…</div>
      ) : (
        <>
          {/* Totales generales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KpiCard label="Usos totales" value={fmtN(data.total.usos)} icon={UtensilsCrossed} tone="bg-emerald-50 text-emerald-800 border-emerald-200" />
            <KpiCard label="Monto total" value={fmtCLP(data.total.monto)} icon={DollarSign} tone="bg-sky-50 text-sky-800 border-sky-200" />
            <KpiCard label="Ticket promedio"
              value={data.total.usos > 0 ? fmtCLP(Math.round(data.total.monto / data.total.usos)) : fmtCLP(0)}
              icon={TrendingUp} tone="bg-amber-50 text-amber-800 border-amber-200" />
          </div>

          {/* Por tipo de servicio */}
          <Section title="Por tipo de servicio" icon={UtensilsCrossed}>
            <TablaSimple
              cols={['Tipo', 'Valor unit.', 'Usos', 'Monto', '%']}
              rows={data.por_tipo.map(t => {
                const pct = data.total.monto > 0 ? Math.round((t.monto / data.total.monto) * 100) : 0;
                return [
                  <span key="a" className="inline-flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR_HEX[t.color] || COLOR_HEX.slate }} />
                    <b>{t.tipo}</b>
                  </span>,
                  fmtCLP(t.valor_unitario),
                  fmtN(t.usos),
                  <b key="b">{fmtCLP(t.monto)}</b>,
                  <span key="c" className="text-xs">{pct}%</span>,
                ];
              })}
              alignRight={[false, true, true, true, true]}
            />
          </Section>

          {/* Detalle por centro de costo × tipo */}
          <Section title="Detalle por Centro de Costo × Tipo" icon={Layers}>
            <TablaSimple
              cols={['Centro de costo', 'Tipo', 'Valor unit.', 'Usos', 'Monto']}
              rows={data.cc_x_tipo.map(r => [
                <span key="a" className="font-mono font-bold text-slate-800">{r.centro}</span>,
                r.tipo,
                fmtCLP(r.valor_unitario),
                fmtN(r.usos),
                <b key="b">{fmtCLP(r.monto)}</b>,
              ])}
              alignRight={[false, false, true, true, true]}
            />
          </Section>

          {/* Por centro de costo (resumen) + por empresa */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Section title="Resumen por Centro de Costo" icon={Layers}>
              <TablaSimple
                cols={['Centro', 'Usos', 'Monto']}
                rows={data.por_centro_costo.map(c => [
                  <span key="a" className="font-mono font-bold">{c.centro}</span>,
                  fmtN(c.usos), <b key="b">{fmtCLP(c.monto)}</b>,
                ])}
                alignRight={[false, true, true]}
              />
            </Section>
            <Section title="Top empresas (Top 20)" icon={Building2}>
              <TablaSimple
                cols={['Empresa', 'Usos', 'Monto']}
                rows={data.por_empresa.map(e => [e.empresa, fmtN(e.usos), <b key="a">{fmtCLP(e.monto)}</b>])}
                alignRight={[false, true, true]}
              />
            </Section>
          </div>

          {/* Serie diaria */}
          <Section title="Serie diaria" icon={Calendar}>
            <SerieDiaria rows={data.por_dia} />
          </Section>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone }) {
  return (
    <div className={`rounded-2xl border p-4 flex items-center gap-4 ${tone}`}>
      <div className="w-11 h-11 rounded-full bg-white/60 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-widest opacity-70">{label}</div>
        <div className="text-2xl font-black">{value}</div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
        <Icon className="w-4 h-4 text-emerald-700" />
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function TablaSimple({ cols, rows, alignRight = [] }) {
  if (!rows || rows.length === 0) {
    return <div className="p-6 text-sm text-slate-400 text-center">Sin datos en el rango.</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-xs">
        <tr>
          {cols.map((c, i) => (
            <th key={i} className={`px-4 py-2 font-semibold ${alignRight[i] ? 'text-right' : 'text-left'}`}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="border-t border-slate-100 hover:bg-slate-50/50">
            {r.map((cell, ci) => (
              <td key={ci} className={`px-4 py-2 ${alignRight[ci] ? 'text-right' : 'text-left'} text-slate-700`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SerieDiaria({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="p-6 text-sm text-slate-400 text-center">Sin datos.</div>;
  }
  const maxMonto = Math.max(...rows.map(r => r.monto || 0));
  const maxUsos = Math.max(...rows.map(r => r.usos || 0));
  const CHART_H = 160; // px
  return (
    <div className="p-4" data-testid="serie-diaria">
      <div className="flex items-end gap-1" style={{ height: `${CHART_H}px` }}>
        {rows.map(r => {
          const barH = maxMonto > 0 ? Math.max(4, Math.round((r.monto / maxMonto) * (CHART_H - 24))) : 4;
          return (
            <div key={r.fecha} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group relative"
                 title={`${r.fecha} · ${r.usos} usos · ${fmtCLP(r.monto)}`}>
              <span className="absolute -top-1 opacity-0 group-hover:opacity-100 text-[10px] font-semibold text-emerald-800 whitespace-nowrap bg-white px-1.5 py-0.5 rounded shadow-sm border border-emerald-100 transition-opacity pointer-events-none z-10">
                {fmtCLP(r.monto)} · {r.usos} usos
              </span>
              <div className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 hover:from-emerald-700 hover:to-emerald-500 rounded-t-sm transition-colors"
                   style={{ height: `${barH}px` }} />
              <div className="text-[9px] text-slate-500 font-mono leading-none">{r.fecha.slice(8, 10)}</div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-slate-400 mt-3 flex justify-between border-t border-slate-100 pt-2">
        <span>{rows[0].fecha.split('-').reverse().join('-')}</span>
        <span>Máx día: <b className="text-emerald-800">{fmtCLP(maxMonto)}</b> · Máx usos: <b className="text-emerald-800">{fmtN(maxUsos)}</b></span>
        <span>{rows[rows.length - 1].fecha.split('-').reverse().join('-')}</span>
      </div>
    </div>
  );
}
